import React, { useEffect, useState } from 'react'
import { Card, Form, Button, Row, Col, DatePicker, Spin, Table } from 'antd'
import BaseListing from 'src/BaseListings'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import './style.css'
import WidgetItemSelector from 'src/widgets/Selectors/WidgetItemSelector'
import { wtFormatRoundedValue } from 'src/helpers/commons/utils'
import { generateGatePassPdf } from 'src/models/inventory_transfer'
import { downloadSodIssuancePdf } from 'src/models/sod_issuance'

const DEFAULT_PAGE_SIZE = 20

const ItemLedgerReport = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()
  const [data, setData] = useState([])
  const [items, setItems] = useState([])
  const [itemId, setItemId] = useState('')
  const [itemName, setItemName] = useState('')
  const [dateRange, setDateRange] = useState([])
  const [openingBalance, setOpeningBalance] = useState(0)
  const [closingBalance, setClosingBalance] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [totalRecords, setTotalRecords] = useState(0)
  const [isItemFiltered, setIsItemFiltered] = useState(false)

  const base = new BaseListing('item_ledger_report')

  useEffect(() => {
    base.dropdownData('items', {}, [setItems], 1)
  }, [])

  const hasValidDateRange = () => Array.isArray(dateRange) && dateRange.length === 2 && dateRange[0] && dateRange[1]

  const getRequestParams = (page = currentPage, size = pageSize) => ({
    from_date: dateRange[0].format('YYYY-MM-DD'),
    to_date: dateRange[1].format('YYYY-MM-DD'),
    item_id: itemId || '',
    _page_index: page,
    _page_size: size,
  })

  const changeValues = (key, val) => {
    if (key === 'item_id') {
      setItemId(val?.value || '')
      setItemName(val?.label || '')
    }
  }

  const loadReportData = (page = currentPage, size = pageSize) => {
    if (!hasValidDateRange()) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }

    setIsLoading(true)

    wtApiCall(
      'reports/item_ledger_report',
      getRequestParams(page, size),
      'get',
      (response) => {
        const transactions = response.data.transactions || {}

        setData(transactions.data || [])
        setCurrentPage(transactions.current_page || page)
        setPageSize(transactions.per_page || size)
        setTotalRecords(transactions.total || 0)
        setOpeningBalance(response.data.opening_balance ?? 0)
        setClosingBalance(response.data.closing_balance ?? 0)
        setIsItemFiltered(Boolean(response.data.is_item_filtered))
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const handleSearch = () => {
    const firstPage = 1
    setCurrentPage(firstPage)
    loadReportData(firstPage, pageSize)
  }

  const handleTableChange = (pagination) => {
    const nextPage = pagination.current || 1
    const nextPageSize = pagination.pageSize || DEFAULT_PAGE_SIZE

    setCurrentPage(nextPage)
    setPageSize(nextPageSize)
    loadReportData(nextPage, nextPageSize)
  }

  const handleSourceModel = (sourceTypeName, sourceModelId) => {
    switch (sourceTypeName) {
      case 'Inventory Transfer':
        generateGatePassPdf(sourceModelId)
        break
      case 'Delivery Issuance':
        downloadSodIssuancePdf(sourceModelId)
        break
      default:
        alert('No PDF available for this transaction type')
        break
    }
  }

  const canOpenSourceDocument = (record) => (
    ['Inventory Transfer', 'Delivery Issuance'].includes(record.source_type_name)
    && Boolean(record.source_model?.id)
  )

  const handleExport = () => {
    if (!hasValidDateRange()) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }

    setIsLoading(true)
    wtApiPostWithContentTypeHandling('reports/item_ledger_report/export', {
      from_date: dateRange[0].format('YYYY-MM-DD'),
      to_date: dateRange[1].format('YYYY-MM-DD'),
      item_id: itemId || '',
    }).finally(() => setIsLoading(false))
  }

  const openingBalanceRow = {
    id: 'opening-balance-row',
    row_type: 'summary',
    summary_label: isItemFiltered ? 'Opening Balance' : 'Opening Balance Total',
    balance: openingBalance,
  }

  const closingBalanceRow = {
    id: 'closing-balance-row',
    row_type: 'summary',
    summary_label: isItemFiltered ? 'Closing Balance' : 'Closing Balance Total',
    balance: closingBalance,
  }

  const hasTransactions = data.length > 0
  const buildGroupedTableData = () => {
    if (isItemFiltered) {
      return hasTransactions ? [openingBalanceRow, ...data, closingBalanceRow] : [openingBalanceRow, closingBalanceRow]
    }

    if (!hasTransactions) {
      return []
    }

    const groupedRows = []
    const groupedData = {}

    data.forEach((row) => {
      const itemKey = String(row.item_id || '')

      if (!groupedData[itemKey]) {
        groupedData[itemKey] = {
          item_code: row.item_code || '',
          item_name: row.item_name || '',
          opening_balance: row.opening_balance || 0,
          transactions: [],
        }
      }

      groupedData[itemKey].transactions.push(row)
    })

    Object.entries(groupedData).forEach(([itemKey, group]) => {
      const itemLabel = [group.item_code, group.item_name].filter(Boolean).join(' - ') || 'Item'
      const lastTransaction = group.transactions[group.transactions.length - 1]

      groupedRows.push({
        id: `opening-balance-row-${itemKey}`,
        row_type: 'summary',
        summary_label: `${itemLabel} Opening Balance`,
        balance: group.opening_balance,
      })

      group.transactions.forEach((transaction) => {
        groupedRows.push(transaction)
      })

      groupedRows.push({
        id: `closing-balance-row-${itemKey}`,
        row_type: 'summary',
        summary_label: `${itemLabel} Closing Balance`,
        balance: lastTransaction?.balance || group.opening_balance,
      })
    })

    return groupedRows
  }

  const tableData = buildGroupedTableData()

  const columns = [
    {
      key: 'sr',
      title: 'Sr#',
      render: (_, record, index) =>
        record.row_type === 'summary' ? '' : ((currentPage - 1) * pageSize) + index,
      width: 70,
    },
    {
      key: 'transaction_date',
      title: 'Date',
      render: (_, record) => (record.row_type === 'summary' ? '' : record.transaction_date),
      width: 120,
    },
    {
      key: 'item_code',
      title: 'Item Code',
      render: (_, record) => (record.row_type === 'summary' ? '' : record.item_code),
      width: 140,
    },
    {
      key: 'item_name',
      title: 'Item Name',
      render: (_, record) => (record.row_type === 'summary' ? record.summary_label : record.item_name),
      width: 220,
    },
    {
      key: 'document_no',
      title: 'Document No',
      width: 150,
      render: (_, record) => {
        if (record.row_type === 'summary') {
          return ''
        }

        const documentNo = record.document_no || record.source_model?.code || ''

        return canOpenSourceDocument(record) && documentNo ? (
          <a href="#/" onClick={(event) => {
            event.preventDefault()
            handleSourceModel(record.source_type_name, record.source_model.id)
          }}>
            {documentNo}
          </a>
        ) : (
          documentNo
        )
      },
    },
    {
      key: 'source_type_name',
      title: 'Transaction Type',
      render: (_, record) => (record.row_type === 'summary' ? '' : record.source_type_name),
      width: 180,
    },
    {
      key: 'warehouse_name',
      title: 'Warehouse',
      render: (_, record) => (record.row_type === 'summary' ? '' : record.warehouse_name),
      width: 160,
    },
    {
      key: 'received_qty',
      title: 'Received Qty',
      width: 130,
      render: (_, record) =>
        record.row_type === 'summary' ? '' : record.quantity > 0 ? Math.abs(record.quantity) : '-',
    },
    {
      key: 'issued_qty',
      title: 'Issued Qty',
      width: 130,
      render: (_, record) =>
        record.row_type === 'summary' ? '' : record.quantity < 0 ? Math.abs(record.quantity) : '-',
    },
    {
      key: 'balance',
      title: 'Balance Qty',
      width: 130,
      render: (_, record) =>
        record.row_type === 'summary'
          ? wtFormatRoundedValue(record.balance || 0, '')
          : record.balance,
    },
    {
      key: 'unit_cost',
      title: 'Unit Cost',
      width: 130,
      render: (_, record) => (record.row_type === 'summary' ? '' : wtFormatRoundedValue(record.unit_cost || 0, '')),
    },
    {
      key: 'total_cost',
      title: 'Total Cost',
      width: 140,
      render: (_, record) => (record.row_type === 'summary' ? '' : wtFormatRoundedValue(record.total_cost || 0, '')),
    },
  ]

  return (
    <div
      className="container-fluid"
      style={{ padding: '1em', backgroundColor: 'white', minHeight: '100vh' }}
    >
      <Form layout="vertical" form={form} onFinish={handleSearch}>
        <Card
          title={
            <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '22px' }}>
              Item Ledger Report
            </div>
          }
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} md={5}>
              <label>Date Range: *</label>
              <DatePicker.RangePicker
                onChange={(value) => setDateRange(value || [])}
                style={{ width: '100%', height: 35 }}
                value={dateRange}
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <label>Item Name:</label>
              <WidgetItemSelector
                items={items}
                onChange={(value) => changeValues('item_id', value)}
                displayValue={itemName}
                reloadDropdownData={() => base.dropdownData('items', {}, [setItems], 1)}
                placeHolder="All Items"
                value={itemId}
              />
            </Col>

            <Col xs={24} sm={24} md={8}>
              <Button
                style={{ marginTop: 30, marginRight: 5 }}
                type="primary"
                htmlType="submit"
                disabled={isLoading}
              >
                Search
              </Button>

              <Button
                style={{ marginTop: 30, backgroundColor: '#4BB543', color: '#fff' }}
                onClick={handleExport}
                disabled={isLoading}
              >
                Export Excel
              </Button>
            </Col>
          </Row>
        </Card>
      </Form>

      <Card style={{ marginTop: 30 }}>
        <Table
          columns={columns}
          dataSource={tableData}
          rowKey={(record, index) => record.id || `${record.item_id}-${record.source_type}-${record.source_id}-${index}`}
          bordered
          loading={{ indicator: <div><Spin /></div>, spinning: isLoading }}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalRecords,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100, 200],
            position: ['bottomCenter'],
          }}
          scroll={{ x: 1500 }}
          rowClassName={(record) => (record.row_type === 'summary' ? 'item-ledger-summary-row' : '')}
        />
      </Card>
    </div>
  )
}

export default ItemLedgerReport
